import { isNumber, isString, object, present, toDate, toSeconds } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { useMemo, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { usePageTitle } from '../../Lib';
import { isOptedIntoNewExperience } from '../../Lib/newExperienceLib';

import {
  formatDate,
  formatDateTime,
  formatUsd,
  kaiPartialIdPresenter,
  normalizeProjectType,
  presentBathCount,
  presentUser,
  propertyIdPresenter,
} from '@kaiizen/kai-presenters';
import { ApiUser, getFlattenedScanNotes } from '@kaiizen/kai-pro-api';
import {
  PhotoSize,
  ScanEditingContextProvider,
  useAuth,
  useAutoSwitchOrgForResource,
  useCacheScanPhotos,
  useCurrentOrg,
  useFeatureFlags,
  useOrgMarket,
  usePhotoGallery,
  usePropertyProject,
  useScan,
  useScanEditingContext,
  useScanEditingContextSlice,
  useUsers,
  useUsersByOrgMarketId,
  useUsersForScan,
} from '@kaiizen/kai-pro-api-react-state';
import { ScanHomeFacts } from '@kaiizen/kai-scan-template-system';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { PageLayout } from '@/Client/Layout';
import { getPropertyIdLabel } from '@/Client/Lib';
import { PROJECT_NOTES_CHARACTER_LIMIT } from '@/Client/noteConstants';
import { ContractorsPanel, EditableScanHeroImage, HistoricalScanRow, PrimaryProjectArtifactStatusCards, ProjectTimeline, StatusPill } from '@/Client/Partials';
import { applyGalleryPhotoOperation, PhotoManagerModal } from '@/Client/Partials/FocusedPhotoGallery';
import {
  ShowNotesProvider,
  useConfirmationModal,
  useNavigateToProject,
  useNavigateToProperty,
  useNavigateToScanTemplate,
  useProjectTypes,
  useShowNotes,
  useToast,
} from '@/Client/State';
import { LAYOUT2, TYPOGRAPHY2 } from '@/Client/Styles';
import {
  Button,
  LabeledCalendarInput,
  LabeledCard,
  LabeledInput,
  LabeledMultiSelect,
  LabeledSelect,
  LabelValueRow,
  presentWas,
  PropsCard,
} from '@/Client/Widgets';
import { GenericLoadingPage } from '../GenericPages';

const TIMELINE_LIST_STYLE = css`
  label: TIMELINE_LIST_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing2}px;
`;

const NO_SCANS_TEXT_STYLE = css`
  label: NO_SCANS_TEXT_STYLE;
  ${TYPOGRAPHY2.small}
  color: var(--text2);
`;

const SUPER_ADMIN_SECTION = css`
  label: SUPER_ADMIN_SECTION;
  display: flex;
  flex-direction: column;
  background: var(--background);
  padding: ${LAYOUT2.spacing2}px;
  gap: ${LAYOUT2.spacing1}px;
  border: 1px solid var(--border);
`;

const PANEL_ROW_STYLE = css`
  label: PANEL_ROW_STYLE;
  display: flex;
  width: 100%;
  flex-direction: row;
  gap: ${LAYOUT2.spacing1}px;
  min-width: 0;
`;

const PROJECT_PAGE_CONTENT_STYLE = css`
  label: PROJECT_PAGE_CONTENT_STYLE;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: ${LAYOUT2.spacing1}px;
  align-items: start;

  @media (max-width: 1400px) {
    grid-template-columns: 1fr;
  }
`;

const PROJECT_PAGE_MAIN_STYLE = css`
  label: PROJECT_PAGE_MAIN_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
  min-width: 0;
`;

const PROJECT_PAGE_ASIDE_STYLE = css`
  label: PROJECT_PAGE_ASIDE_STYLE;
  min-width: 0;

  @media (min-width: 1401px) {
    position: sticky;
    top: 0;
    align-self: start;
    max-height: calc(100vh - ${LAYOUT2.spacing2}px);
    overflow: auto;
  }
`;

const parseNonNegativeInt = (value: any): number => {
  if (isNumber(value)) return value < 0 ? 0 : value;
  if (!isString(value)) return 0;
  const int = parseInt(value);
  if (isNaN(int) || int < 0) return 0;
  return int;
};

const parseNonNegativeFloat = (value: any): number => {
  if (isNumber(value)) return value < 0 ? 0 : value;
  if (!isString(value)) return 0;
  const float = parseFloat(value);
  if (isNaN(float) || float < 0) return 0;
  return float;
};

const INPUT_ROW_STYLE = css`
  label: INPUT_ROW_STYLE;
  display: flex;
  flex-direction: row;
  gap: ${LAYOUT2.spacing1}px;
  width: 100%;
`;

const EDIT_FACT_FIELD_STYLE = css`
  label: EDIT_FACT_FIELD_STYLE;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${LAYOUT2.spacingHalf}px;
  width: 100%;
  min-width: 0;
`;

const EDIT_FACT_PREVIOUS_VALUE_STYLE = css`
  label: EDIT_FACT_PREVIOUS_VALUE_STYLE;
  ${TYPOGRAPHY2.small}
  color: var(--red);
  white-space: nowrap;
`;

const REVERT_BUTTON_STYLE = css`
  label: REVERT_BUTTON_STYLE;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: var(--text2);

  &:hover {
    color: var(--text);
  }
`;

const RevertIcon = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M3 7v6h6' />
    <path d='M21 17a9 9 0 0 0-15-6.7L3 13' />
  </svg>
);

type PropertyFactsDraft = {
  propertyId: string;
  bedroomCount: number | undefined;
  fullBathCount: number | undefined;
  halfBathCount: number | undefined;
  hasGarage: boolean | undefined;
  squareFeet: number | undefined;
  acres: number | undefined;
  vintage: number | undefined;
};

const PropertyInfoCard = () => {
  // Editing is draft-based: Edit seeds the draft from the property record with any
  // field-proposed scan-root values merged in, so pending proposals pre-populate the
  // form (Edit → Save accepts them all). Cancel discards the draft; Save persists it
  // to the property and clears every scan-root proposal — the web user's save is the
  // final answer, so no proposal survives it.
  const [draft, setDraft] = useState<PropertyFactsDraft | undefined>();
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const { userCan } = useAuth();
  const { property, project, propertyMutators, projectIsEditable, scanRoot, scanDataMutators } = useScanEditingContextSlice([
    'property',
    'project',
    'propertyMutators',
    'projectIsEditable',
    'scanRoot',
    'scanDataMutators',
  ]);
  const { currentOrg } = useCurrentOrg();
  const { orgMarket } = useOrgMarket(property?.orgMarketId);
  const firstScan = project?.firstScan;
  const navigateToProperty = useNavigateToProperty();
  if (!property || !firstScan) return null;
  const canEditProperty = projectIsEditable && userCan('update', 'OrgProperty');
  const countItems = Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
  // Baths legitimately go to zero (e.g. 0 half baths); beds keep the 1-based list.
  const bathCountItems = Array.from({ length: 11 }, (_, i) => ({ value: String(i), label: String(i) }));

  // Home facts (beds/baths/garage/etc.) can be revised in the field during a scan.
  // Until the change is applied to the property record (post scope review), render
  // the proposed value bold with a red "was …" note of the current value beside it.
  // `proposed` reads the scan-root override; `current` reads the saved property
  // record — both are persisted state (never local edit/form state).
  const proposed = scanRoot;
  const numFact = (value: number | null | undefined) => present(value) ? String(value) : undefined;
  const bathFact = (full: number | null | undefined, half: number | null | undefined, total: number | null | undefined) => {
    const presented = presentBathCount(full, half, total);
    return presented === '--' ? undefined : presented;
  };
  const acresFact = (value: number | null | undefined) => present(value) ? parseFloat(String(value)).toFixed(2) : undefined;
  const boolFact = (value: boolean | null | undefined) => value == null ? undefined : value ? 'Yes' : 'No';

  const propertyFacts = property.customerMetadata;

  const homeFactRows = [
    { label: 'Beds', current: numFact(propertyFacts?.bedroomCount), proposedValue: numFact(proposed?.bedroomCount) },
    {
      label: 'Baths',
      current: bathFact(propertyFacts?.fullBathCount, propertyFacts?.halfBathCount, propertyFacts?.bathroomCount),
      proposedValue: proposed ? bathFact(proposed.fullBathCount, proposed.halfBathCount, proposed.bathroomCount) : undefined,
    },
    { label: 'Garage', current: boolFact(propertyFacts?.hasGarage), proposedValue: boolFact(proposed?.hasGarage) },
    { label: 'Square Feet', current: numFact(propertyFacts?.squareFeet), proposedValue: numFact(proposed?.squareFeet) },
    { label: 'Acreage', current: acresFact(propertyFacts?.acres), proposedValue: acresFact(proposed?.acres) },
    { label: 'Year Built', current: numFact(propertyFacts?.vintage), proposedValue: numFact(proposed?.vintage) },
  ];
  const factHasProposedUpdate = ({ current, proposedValue }: { current: string | undefined; proposedValue: string | undefined }) =>
    present(proposedValue) && proposedValue !== current;
  const hasProposedUpdates = homeFactRows.some(factHasProposedUpdate);
  const homeFactItem = (row: { label: string; current: string | undefined; proposedValue: string | undefined }) =>
    factHasProposedUpdate(row)
      ? <LabelValueRow key={row.label} label={row.label} value={row.proposedValue!} previousValue={row.current ?? ''} uppercaseLabel />
      : { label: row.label, value: (present(row.proposedValue) ? row.proposedValue : row.current) ?? '—' };

  const startEditing = () =>
    setDraft({
      propertyId: propertyIdPresenter(property),
      bedroomCount: proposed?.bedroomCount ?? propertyFacts?.bedroomCount ?? undefined,
      fullBathCount: proposed?.fullBathCount ?? propertyFacts?.fullBathCount ?? undefined,
      halfBathCount: proposed?.halfBathCount ?? propertyFacts?.halfBathCount ?? undefined,
      hasGarage: proposed?.hasGarage ?? propertyFacts?.hasGarage ?? undefined,
      squareFeet: proposed?.squareFeet ?? propertyFacts?.squareFeet ?? undefined,
      acres: proposed?.acres ?? propertyFacts?.acres ?? undefined,
      vintage: proposed?.vintage ?? propertyFacts?.vintage ?? undefined,
    });

  const updateDraft = (updates: Partial<PropertyFactsDraft>) => setDraft(currentDraft => currentDraft && { ...currentDraft, ...updates });

  const saveDraft = async () => {
    // Read through the ref: input debounce flushes on blur, which lands just before
    // this click handler runs, and we must save that final value.
    const currentDraft = draftRef.current;
    if (!currentDraft) return;
    const homeFacts: ScanHomeFacts = {
      bedroomCount: currentDraft.bedroomCount,
      fullBathCount: currentDraft.fullBathCount,
      halfBathCount: currentDraft.halfBathCount,
      bathroomCount: currentDraft.fullBathCount != null || currentDraft.halfBathCount != null
        ? (currentDraft.fullBathCount ?? 0) + (currentDraft.halfBathCount ?? 0)
        : undefined,
      hasGarage: currentDraft.hasGarage,
      squareFeet: currentDraft.squareFeet,
      acres: currentDraft.acres,
      vintage: currentDraft.vintage,
    };
    await propertyMutators.update(property.id, { customerMetadata: { ...homeFacts, propertyId: currentDraft.propertyId } });
    // The web user's save is the final answer: erase every scan-root proposal (null
    // erases on merge) so no proposed-change indicators survive the save.
    await scanDataMutators.updateScan({
      bedroomCount: null,
      bathroomCount: null,
      fullBathCount: null,
      halfBathCount: null,
      hasGarage: null,
      squareFeet: null,
      acres: null,
      vintage: null,
    });
    setDraft(undefined);
  };

  const factChanged = (draftValue: number | boolean | string | undefined, propertyValue: number | boolean | string | null | undefined) =>
    draftValue !== (propertyValue ?? undefined);

  const draftChanged = !!draft && (
    factChanged(draft.propertyId, propertyIdPresenter(property))
    || factChanged(draft.bedroomCount, propertyFacts?.bedroomCount)
    || factChanged(draft.fullBathCount, propertyFacts?.fullBathCount)
    || factChanged(draft.halfBathCount, propertyFacts?.halfBathCount)
    || factChanged(draft.hasGarage, propertyFacts?.hasGarage)
    || factChanged(draft.squareFeet, propertyFacts?.squareFeet)
    || factChanged(draft.acres, propertyFacts?.acres)
    || factChanged(draft.vintage, propertyFacts?.vintage)
  );

  // While editing, a field whose draft value differs from the property record shows a
  // red "was …" note of the record's value plus a revert affordance to restore it.
  const editableFactItem = (key: string, previousValue: string | undefined, changed: boolean, revert: () => void, input: React.ReactNode) => (
    <div key={key} css={EDIT_FACT_FIELD_STYLE}>
      {changed && (
        <>
          <span css={EDIT_FACT_PREVIOUS_VALUE_STYLE} title='Current value on the property record'>{presentWas(previousValue)}</span>
          <div css={REVERT_BUTTON_STYLE} title="Revert to the property record's value" onClick={revert}>
            <RevertIcon />
          </div>
        </>
      )}
      {input}
    </div>
  );

  return (
    <PropsCard
      title='Property Info'
      uppercaseLabel
      columnCount={2}
      alignLabels='center'
      buttons={[
        { label: 'View Property', onClick: () => navigateToProperty({ propertyId: property.id }) },
        ...(canEditProperty
          ? draft
            ? [
              { label: 'Cancel', onClick: () => setDraft(undefined) },
              {
                label: hasProposedUpdates ? 'Accept Updates' : 'Save',
                buttonStyle: 'lemon' as const,
                disabled: !hasProposedUpdates && !draftChanged,
                onClick: saveDraft,
              },
            ]
            : [
              hasProposedUpdates
                ? { label: 'Review Updates', buttonStyle: 'lemon' as const, onClick: startEditing }
                : { label: 'Edit', onClick: startEditing },
            ]
          : []),
      ]}
      items={draft
        ? [
          editableFactItem(
            'propertyId',
            propertyIdPresenter(property),
            factChanged(draft.propertyId, propertyIdPresenter(property)),
            () => updateDraft({ propertyId: propertyIdPresenter(property) }),
            <LabeledInput
              label={getPropertyIdLabel(currentOrg?.name)}
              size='medium'
              value={draft.propertyId}
              onUpdate={propertyId => updateDraft({ propertyId })}
            />,
          ),
          editableFactItem(
            'beds',
            numFact(propertyFacts?.bedroomCount),
            factChanged(draft.bedroomCount, propertyFacts?.bedroomCount),
            () => updateDraft({ bedroomCount: propertyFacts?.bedroomCount ?? undefined }),
            <LabeledSelect
              label='Beds'
              size='medium'
              value={String(draft.bedroomCount ?? '')}
              items={countItems}
              onUpdate={value => updateDraft({ bedroomCount: parseNonNegativeInt(value) })}
            />,
          ),
          <div css={INPUT_ROW_STYLE} key='baths'>
            {editableFactItem(
              'fullBaths',
              numFact(propertyFacts?.fullBathCount),
              factChanged(draft.fullBathCount, propertyFacts?.fullBathCount),
              () => updateDraft({ fullBathCount: propertyFacts?.fullBathCount ?? undefined }),
              <LabeledSelect
                label='Full Baths'
                size='medium'
                value={String(draft.fullBathCount ?? '')}
                items={bathCountItems}
                onUpdate={value => updateDraft({ fullBathCount: parseNonNegativeInt(value) })}
              />,
            )}
            {editableFactItem(
              'halfBaths',
              numFact(propertyFacts?.halfBathCount),
              factChanged(draft.halfBathCount, propertyFacts?.halfBathCount),
              () => updateDraft({ halfBathCount: propertyFacts?.halfBathCount ?? undefined }),
              <LabeledSelect
                label='Half Baths'
                size='medium'
                value={String(draft.halfBathCount ?? '')}
                items={bathCountItems}
                onUpdate={value => updateDraft({ halfBathCount: parseNonNegativeInt(value) })}
              />,
            )}
          </div>,
          editableFactItem(
            'garage',
            propertyFacts?.hasGarage == null ? undefined : propertyFacts.hasGarage ? 'Yes' : 'No',
            factChanged(draft.hasGarage, propertyFacts?.hasGarage),
            () => updateDraft({ hasGarage: propertyFacts?.hasGarage ?? undefined }),
            <LabeledSelect
              label='Garage'
              size='medium'
              value={draft.hasGarage == null ? '' : draft.hasGarage ? 'Yes' : 'No'}
              placeholder='Select...'
              items={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]}
              onUpdate={value => updateDraft({ hasGarage: value === 'Yes' })}
            />,
          ),
          editableFactItem(
            'squareFeet',
            numFact(propertyFacts?.squareFeet),
            factChanged(draft.squareFeet, propertyFacts?.squareFeet),
            () => updateDraft({ squareFeet: propertyFacts?.squareFeet ?? undefined }),
            <LabeledInput
              label='Square Feet'
              size='medium'
              type='number'
              value={String(draft.squareFeet ?? '')}
              onUpdate={value => updateDraft({ squareFeet: parseNonNegativeInt(value) })}
            />,
          ),
          editableFactItem(
            'acres',
            acresFact(propertyFacts?.acres),
            factChanged(draft.acres, propertyFacts?.acres),
            () => updateDraft({ acres: propertyFacts?.acres ?? undefined }),
            <LabeledInput
              label='Acreage'
              size='medium'
              type='number'
              value={String(draft.acres ?? '')}
              onUpdate={value => updateDraft({ acres: parseNonNegativeFloat(value) })}
            />,
          ),
          editableFactItem(
            'vintage',
            numFact(propertyFacts?.vintage),
            factChanged(draft.vintage, propertyFacts?.vintage),
            () => updateDraft({ vintage: propertyFacts?.vintage ?? undefined }),
            <LabeledInput
              label='Year Built'
              size='medium'
              type='number'
              value={String(draft.vintage ?? '')}
              onUpdate={value => updateDraft({ vintage: parseNonNegativeInt(value) })}
            />,
          ),
        ]
        : [
          { label: getPropertyIdLabel(currentOrg?.name), value: propertyIdPresenter(property) },
          ...homeFactRows.map(homeFactItem),
          { label: 'Market', value: orgMarket?.name },
        ]}
    />
  );
};

const AccessInfoCard = () => {
  const [editing, setEditing] = useState(false);
  const { userCan } = useAuth();
  const { property, project, scanMutators, projectIsEditable, scan } = useScanEditingContextSlice([
    'property',
    'project',
    'scanMutators',
    'projectIsEditable',
    'scan',
  ]);
  if (!property || !scan) return null;
  const canEditScan = projectIsEditable && userCan('update', 'Scan');
  return (
    <PropsCard
      title='Access Info'
      uppercaseLabel
      alignLabels='center'
      buttons={canEditScan
        ? [
          { label: editing ? 'Done' : 'Edit', onClick: () => setEditing(!editing) },
        ]
        : []}
      items={editing
        ? [
          <div css={INPUT_ROW_STYLE} key='accessInfo'>
            <LabeledInput
              label='Gate Code'
              size='small'
              value={String(scan.customerMetadata?.gateCode ?? '')}
              onUpdate={value => scanMutators.update(scan.id, { customerMetadata: { gateCode: value } })}
            />
            <LabeledInput
              label='Door Code'
              size='small'
              value={String(scan.customerMetadata?.doorCode ?? '')}
              onUpdate={value => scanMutators.update(scan.id, { customerMetadata: { doorCode: value } })}
            />
          </div>,
        ]
        : [
          { label: 'Gate Code', value: scan.customerMetadata?.gateCode },
          { label: 'Door Code', value: scan.customerMetadata?.doorCode },
        ]}
    />
  );
};

const ProjectInfoCard = () => {
  const { isSuperAdmin, userCan } = useAuth();
  const { cachePhotos } = usePhotoGallery();
  const [cachingPhotos, setCachingPhotos] = useState(false);
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);
  const { property, project, projectMutators, scanMutators, projectIsEditable, scanRoot } = useScanEditingContextSlice([
    'property',
    'project',
    'projectMutators',
    'scanMutators',
    'projectIsEditable',
    'scanRoot',
  ]);
  const { scan: firstScan } = useScan(project?.firstScan);
  const canEditProject = projectIsEditable && userCan('update', 'PropertyProject');
  const { tryWithErrorToast } = useToast();
  const { usersForScan, usersForScanMutators, refetch } = useUsersForScan(firstScan?.id);
  const { users } = useUsers();
  const { users: usersByMarket } = useUsersByOrgMarketId(property?.orgMarketId ?? undefined);
  const { getProjectTypeLabel, getProjectTypeOptions } = useProjectTypes();

  const { orderedUserIdsForScan, userItems, managerItems, orderedManagerIds } = useMemo(
    () => {
      if (!users || !usersForScan) return { orderedUserIdsForScan: undefined, userItems: undefined, managerItems: undefined, orderedManagerIds: undefined };
      const presentedUserNamesByUserId = object(users, { withKey: user => user.id, with: (user: ApiUser) => presentUser(user) });

      const fieldUserSource = usersByMarket ?? users;
      const marketUserItems = fieldUserSource.map(user => ({
        value: user.id,
        label: presentedUserNamesByUserId[user.id] ?? presentUser(user),
      }));

      return {
        orderedUserIdsForScan: [...usersForScan]
          .sort((a, b) =>
            (presentedUserNamesByUserId[a.id] && presentedUserNamesByUserId[b.id])
              ? presentedUserNamesByUserId[a.id].localeCompare(presentedUserNamesByUserId[b.id])
              : 0
          )
          .map(user => user.id),

        userItems: marketUserItems,
        managerItems: marketUserItems,
        orderedManagerIds: (project?.managerIds ?? [])
          .slice()
          .sort((a, b) =>
            (presentedUserNamesByUserId[a] && presentedUserNamesByUserId[b])
              ? presentedUserNamesByUserId[a].localeCompare(presentedUserNamesByUserId[b])
              : 0
          ),
      };
    },
    [users, usersByMarket, usersForScan, project?.managerIds],
  );

  if (!project || !firstScan || !orderedUserIdsForScan || !orderedManagerIds) return null;

  const updateProject = async (
    field: string,
    updates: Parameters<typeof projectMutators.update>[1],
  ) => {
    await projectMutators.update(project.id, updates);
    trackEvent(ANALYTICS_EVENTS.projectUpdated, { projectId: project.id, field });
  };

  const updateScope = async (
    field: string,
    updates: Parameters<typeof scanMutators.update>[1],
  ) => {
    await scanMutators.update(firstScan.id, updates);
    trackEvent(ANALYTICS_EVENTS.scanUpdated, { scanId: firstScan.id, field });
  };

  const selectedProjectType = project.customerMetadata?.projectType
    ? normalizeProjectType(project.customerMetadata.projectType)
    : '';
  const projectTypeOptions = getProjectTypeOptions(selectedProjectType);

  return (
    <PropsCard
      title='Project Details'
      uppercaseLabel
      alignLabels='center'
      buttons={[
        canEditProject && {
          label: editing ? 'Done' : 'Edit',
          onClick: () => setEditing(!editing),
        },
      ]}
      onButtonClick={() => setEditing(!editing)}
      items={editing
        ? [
          <LabeledInput
            key='projectName'
            label='Project Name'
            size='small'
            value={project.name}
            onUpdate={value => tryWithErrorToast('updating project name', () => updateProject('name', { name: value }))}
          />,
          canEditProject
            ? (
              <LabeledSelect
                key='projectTypeSelect'
                label='Project Type'
                size='small'
                value={selectedProjectType}
                items={projectTypeOptions}
                onUpdate={value => tryWithErrorToast('changing project type', () => updateProject('type', { customerMetadata: { projectType: value } }))}
              />
            )
            : (
              <LabeledInput
                key='projectType'
                label='Project Type'
                size='small'
                value={getProjectTypeLabel(project.customerMetadata?.projectType) ?? '—'}
                disabled
              />
            ),
          <LabeledInput
            key='budget'
            label='Budget'
            size='small'
            value={String(project.customerMetadata?.budget ?? '')}
            onUpdate={value =>
              tryWithErrorToast('updating budget', () => updateProject('budget', { customerMetadata: { budget: parseNonNegativeFloat(value) } }))}
          />,
          // etc date - use date picker
          <LabeledCalendarInput
            key='projectEndDate'
            label='Project End Date'
            size='small'
            value={project.customerMetadata?.dueDate ? toDate(project.customerMetadata?.dueDate).toISOString() : ''}
            onUpdate={value =>
              tryWithErrorToast('updating project end date', () => updateProject('due_date', { customerMetadata: { dueDate: toDate(value) } }))}
          />,
          <LabeledCalendarInput
            key='scopeDueDate'
            label='Scope Due Date'
            size='small'
            value={firstScan.customerMetadata?.dueDate ? toDate(firstScan.customerMetadata?.dueDate).toISOString() : ''}
            onUpdate={value => tryWithErrorToast('updating scope due date', () => updateScope('due_date', { customerMetadata: { dueDate: toDate(value) } }))}
          />,
          <LabeledMultiSelect
            key='managers'
            label='Project Managers'
            size='small'
            width='100%'
            value={orderedManagerIds}
            items={managerItems}
            onAddItem={async userId => {
              await tryWithErrorToast('assigning project manager', async () => {
                const newIds = [...orderedManagerIds, userId];
                await updateProject('manager_added', { managerIds: newIds });
              });
            }}
            onRemoveItem={async userId => {
              await tryWithErrorToast('removing project manager', async () => {
                const newIds = orderedManagerIds.filter(id => id !== userId);
                if (newIds.length === 0) {
                  showToast('At least one project manager is required', 'error');
                  return;
                }
                await updateProject('manager_removed', { managerIds: newIds });
              });
            }}
          />,
          <LabeledMultiSelect
            key='fieldUsers'
            label='Field User(s)'
            size='small'
            width='100%'
            value={orderedUserIdsForScan}
            items={userItems}
            onAddItem={async userId => {
              await tryWithErrorToast('assigning field user', async () => {
                await usersForScanMutators.assignUserToScan(userId, firstScan.id);
                refetch();
                trackEvent(ANALYTICS_EVENTS.scanUpdated, { scanId: firstScan.id, field: 'field_user_added' });
              });
            }}
            onRemoveItem={async userId => {
              await tryWithErrorToast('removing field user', async () => {
                await usersForScanMutators.removeUserFromScan(userId, firstScan.id);
                refetch();
                trackEvent(ANALYTICS_EVENTS.scanUpdated, { scanId: firstScan.id, field: 'field_user_removed' });
              });
            }}
          />,
        ]
        : [
          { label: 'Project Name', value: project.name },
          { label: 'Project ID', value: kaiPartialIdPresenter(project.id) },
          { label: 'Project Type', value: getProjectTypeLabel(project.customerMetadata?.projectType) ?? '—' },
          { label: 'Budget', value: formatUsd(project.customerMetadata?.budget) },
          { label: 'Project End Date', value: formatDate(project.customerMetadata?.dueDate) },
          { label: 'Scope Due Date', value: formatDate(firstScan.customerMetadata?.dueDate) },
          {
            label: 'Project Managers',
            value: orderedManagerIds.length === 0
              ? '—'
              : orderedManagerIds.length === 1
              ? (managerItems?.find(u => u.value === orderedManagerIds[0])?.label ?? '—')
              : `${managerItems?.find(u => u.value === orderedManagerIds[0])?.label?.split(' ')[0] ?? ''} + ${orderedManagerIds.length - 1} more`,
          },
          { label: 'Field Agent', value: usersForScan?.map(user => user.name).join(', ') ?? '' },
          { label: 'Last Updated', value: formatDateTime(project.updatedAt) },
        ]}
    />
  );
};

export const ProjectDispatchNotesCard = () => {
  const [editing, setEditing] = useState(false);
  const { userCan } = useAuth();
  const { project, projectMutators, projectIsEditable } = useScanEditingContextSlice(['project', 'projectMutators', 'projectIsEditable']);
  if (!project) return null;
  const canEditProject = projectIsEditable && userCan('update', 'PropertyProject');
  return (
    <LabeledCard
      title='Project Dispatch Notes'
      buttons={canEditProject
        ? [
          { label: editing ? 'Done' : 'Edit', onClick: () => setEditing(!editing) },
        ]
        : []}
    >
      {editing
        ? (
          <LabeledInput
            multiline
            rows={6}
            label='Project Dispatch Notes'
            value={project.description ?? ''}
            placeholder='--'
            maxLength={PROJECT_NOTES_CHARACTER_LIMIT}
            showCharacterCount
            onUpdate={value => {
              void projectMutators.update(project.id, { description: value ?? '' }).then(() =>
                trackEvent(ANALYTICS_EVENTS.projectUpdated, { projectId: project.id, field: 'dispatch_notes' })
              );
            }}
          />
        )
        : <div style={{ whiteSpace: 'pre-wrap' }}>{project.description ?? ''}</div>}
    </LabeledCard>
  );
};

export const AllNotesCard = () => {
  const { project, scanNotes, scanRoot, scanIsEditable, scanDataMutators } = useScanEditingContextSlice([
    'project',
    'scanNotes',
    'scanRoot',
    'scanIsEditable',
    'scanDataMutators',
  ]);
  const { showNotesEditor } = useShowNotes();
  const [photoManagerOpen, setPhotoManagerOpen] = useState(false);
  if (!project) return null;
  const notes = getFlattenedScanNotes(scanNotes);
  const lastNote = notes.reduce<(typeof notes)[number] | undefined>(
    (latest, note) => !latest || toSeconds(note.createdAt) > toSeconds(latest.createdAt) ? note : latest,
    undefined,
  );
  return (
    <>
      <PropsCard
        title='Notes'
        uppercaseLabel
        buttons={[
          { label: 'See Notes', onClick: () => showNotesEditor({}) },
          { label: 'See Photos', onClick: () => setPhotoManagerOpen(true) },
        ]}
        items={[
          { label: 'Number of Notes', value: notes.length },
          { label: 'Last Note', value: lastNote ? formatDateTime(lastNote.createdAt) : '--' },
        ]}
      />
      {photoManagerOpen && scanRoot
        ? (
          <PhotoManagerModal
            applyPhotoOperation={async operation => {
              await scanDataMutators.setScan(current => applyGalleryPhotoOperation(current, operation));
            }}
            groups={scanRoot.groups ?? []}
            onClose={() => setPhotoManagerOpen(false)}
            scanIsEditable={scanIsEditable}
            scanRoot={scanRoot}
            /* The project overview has no group or task in hand, so the manager opens
               on the whole scan — every group and task, the same view the scope-level
               photo strip opens. */
            scope={{}}
          />
        )
        : null}
    </>
  );
};

const CachePhotosButton = ({ size = 2048 }: { size?: PhotoSize }) => {
  const { scanRoot } = useScanEditingContextSlice(['scanRoot']);
  const [active, setActive] = useState(false);
  const photoIds = scanRoot?.optimized.allPhotoIds ?? [];
  // Flipping `active` from false → a defined arg turns the hook on; it pre-warms
  // the cache and reports live progress.
  const status = useCacheScanPhotos(active && scanRoot ? { scanId: scanRoot.id, photoIds, size } : undefined);
  const cachedPhotoCount = Math.round((status?.progress ?? 0) * photoIds.length);
  return (
    <Button onClick={() => setActive(true)} buttonStyle='blue'>
      Cache {size} Photos (cached: {cachedPhotoCount} / {photoIds.length})
    </Button>
  );
};

export const ProjectPageInner = () => {
  const { userCan, isSuperAdmin } = useAuth();
  const { scanId, scanTemplateId, scanTemplateAt, scanRoot, project, property, projectIsEditable, projectMutators, scans } = useScanEditingContext();
  usePageTitle(property?.address ? `${[property.address, project?.name].filter(Boolean).join(' · ')}` : undefined);
  const navigateToScanTemplate = useNavigateToScanTemplate();
  const navigateToProject = useNavigateToProject();
  const navigateToProperty = useNavigateToProperty();
  const confirm = useConfirmationModal();
  const { cachePhotos } = usePhotoGallery();

  const scansDescending = useMemo(
    () => [...(scans ?? [])].sort((a, b) => toSeconds(b.createdAt) - toSeconds(a.createdAt)),
    [scans],
  );

  const onReactivateProject = () => {
    if (!project || !property) return;
    confirm({
      title: 'Reactivate Project',
      message: 'Are you sure you want to reactivate this project?',
      confirmLabel: 'Reactivate',
      onConfirm: async () => {
        await projectMutators.update(project.id, { status: 'active' });
        trackEvent(ANALYTICS_EVENTS.projectUpdated, { projectId: project.id, field: 'reactivated' });
        // total hack to get around us getting stuck in the <GenericLoadingPage />
        navigateToProperty({ propertyId: property.id });
        setTimeout(() => {
          navigateToProject({ projectId: project.id });
        }, 50);
      },
    });
  };

  const latestScan = project?.latestScan;

  if (!project || !property || !scanId || !latestScan || !scanRoot || !scanTemplateId) return <GenericLoadingPage title='Project' />;

  return (
    <PageLayout
      title={`${[property?.address, project?.name].filter(Boolean).join(' - ')}`}
      titleIcons={[<StatusPill key='projectStatusPill' statusTypeText={project.status === 'active' ? 'Active' : 'Closed'} color='charcoal' tight />]}
      contentLayoutStyling={'standard'}
      subTitle={'Project Overview'}
      subTitleButtons={!projectIsEditable && userCan('update', 'PropertyProject')
        ? <Button color='bluePrimary' onClick={onReactivateProject}>Reactivate Project</Button>
        : undefined}
    >
      <div css={PROJECT_PAGE_CONTENT_STYLE}>
        <div css={PROJECT_PAGE_MAIN_STYLE}>
          <div css={PANEL_ROW_STYLE}>
            <div style={{ flex: 1 }}>
              <EditableScanHeroImage />
            </div>
            <div style={{ flex: 1 }}>
              <ProjectInfoCard />
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: LAYOUT2.spacing1 }}>
              <AccessInfoCard />
              <AllNotesCard />
            </div>
          </div>

          <PrimaryProjectArtifactStatusCards
            scans={scansDescending}
          />

          <ContractorsPanel />

          <div css={PANEL_ROW_STYLE}>
            <div style={{ flex: 2 }}>
              <PropertyInfoCard />
            </div>
            <div style={{ flex: 1 }}>
              <ProjectDispatchNotesCard />
            </div>
          </div>

          {(() => {
            const isHistoricalScan = (scan: typeof scansDescending[0], index: number) => {
              return index !== 0 && scan.scanType !== 'progress';
            };
            const historicalDocumentCount = scansDescending.filter(isHistoricalScan).length;
            return (
              <LabeledCard title={`Historical Documents (${historicalDocumentCount})`}>
                <div css={TIMELINE_LIST_STYLE}>
                  {scansDescending.length > 0
                    ? scansDescending.map((scan, index) => {
                      if (!isHistoricalScan(scan, index)) {
                        return null;
                      }
                      return (
                        <HistoricalScanRow
                          key={scan.id}
                          scan={scan}
                          latestScan={latestScan}
                          isMostRecentApprovedScan={index === 0}
                        />
                      );
                    })
                    : <div css={NO_SCANS_TEXT_STYLE}>No scans available</div>}
                </div>
              </LabeledCard>
            );
          })()}

          <div css={PANEL_ROW_STYLE}>
            <div style={{ flex: 1 }}>
              <PropsCard
                title='Project Template'
                uppercaseLabel
                buttonLabel='View Template'
                onButtonClick={() => navigateToScanTemplate({ scanTemplateId })}
                items={[
                  { label: 'Name', value: scanRoot.template.name },
                  { label: 'Version', value: formatDate(scanTemplateAt) },
                ]}
              />
            </div>
          </div>
        </div>

        <aside css={PROJECT_PAGE_ASIDE_STYLE}>
          <ProjectTimeline projectId={project.id} />
        </aside>
        {isSuperAdmin && (
          <div css={SUPER_ADMIN_SECTION}>
            <CachePhotosButton size={256} />
            <CachePhotosButton size={512} />
            <CachePhotosButton size={1024} />
            <CachePhotosButton size={2048} />
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export const ProjectPage = () => {
  let { projectId } = useParams();
  const { propertyProject: project, error, isError } = usePropertyProject(projectId);
  const { featureFlags } = useFeatureFlags();

  useAutoSwitchOrgForResource(
    { error: isError ? error : undefined, propertyProjectId: projectId },
  );

  const navigateToProperty = useNavigateToProperty();

  if (!project) return <GenericLoadingPage title='Project' loadingMessage={isError ? 'Error loading project' : undefined} />;
  if (!project.latestScan) {
    return (
      <GenericLoadingPage title='Project' loadingMessage='This project has no scans. If you feel this is in error, please contact support.'>
        <Button
          onClick={() => {
            navigateToProperty({ propertyId: project.orgPropertyId });
          }}
        >
          View Property
        </Button>
      </GenericLoadingPage>
    );
  }

  if (isOptedIntoNewExperience(featureFlags) && featureFlags.turnBasedHandoff) {
    return <Navigate to={`/scan/${project.latestScan.id}/overview`} replace />;
  }

  return (
    <ScanEditingContextProvider scanId={project.latestScan.id} userHasAdminScanAccess={true}>
      <ShowNotesProvider>
        <ProjectPageInner />
      </ShowNotesProvider>
    </ScanEditingContextProvider>
  );
};
